import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { Badge, Button } from 'react-bootstrap';
import AppContext from 'context/Context';

const FORMULA_OPERATORS = ['+', '-', '*', '/', '(', ')'];
const FIELD_TOKEN_PREFIX = 'key';
const POSITIVE_NUMBER_TYPING_PATTERN = /^\d*\.?\d*$/;

const TOKEN_KIND = {
  field: 'field',
  operator: 'operator',
  number: 'number'
};

const TOKEN_BADGE_BG = {
  [TOKEN_KIND.field]: 'primary',
  [TOKEN_KIND.operator]: 'secondary',
  [TOKEN_KIND.number]: 'info'
};

const parseFormulaTokens = formulaWithIds =>
  (formulaWithIds ?? '')
    .split(' ')
    .filter(part => part !== '')
    .map(part => {
      if (part.startsWith(FIELD_TOKEN_PREFIX)) {
        return {
          kind: TOKEN_KIND.field,
          fieldId: Number(part.slice(FIELD_TOKEN_PREFIX.length))
        };
      }
      if (FORMULA_OPERATORS.includes(part)) {
        return { kind: TOKEN_KIND.operator, value: part };
      }
      return { kind: TOKEN_KIND.number, value: part };
    });

const normalizePositiveNumberText = numberText => {
  const parsedNumber = Number(numberText);
  return Number.isFinite(parsedNumber) && parsedNumber > 0
    ? String(parsedNumber)
    : null;
};

export default function SicFormulaEditor({ formulaWithIds, fields, onChange }) {
  const {
    config: { isDark }
  } = useContext(AppContext);
  const [tokens, setTokens] = useState(() =>
    parseFormulaTokens(formulaWithIds)
  );
  const [numberText, setNumberText] = useState('');
  const borderColor = isDark ? '#4a4f58' : '#c4c9d0';

  const fieldNameById = Object.fromEntries(
    fields.map(field => [field.id, field.name])
  );
  const tokenLabel = token =>
    token.kind === TOKEN_KIND.field
      ? fieldNameById[token.fieldId] ?? `Unknown (#${token.fieldId})`
      : token.value;

  const applyTokens = nextTokens => {
    setTokens(nextTokens);
    onChange(
      nextTokens.map(tokenLabel).join(' '),
      nextTokens
        .map(token =>
          token.kind === TOKEN_KIND.field
            ? `${FIELD_TOKEN_PREFIX}${token.fieldId}`
            : token.value
        )
        .join(' ')
    );
  };

  const appendToken = token => applyTokens([...tokens, token]);

  const removeTokenAt = index =>
    applyTokens(tokens.filter((_, tokenIndex) => tokenIndex !== index));

  const handleNumberTextChange = e => {
    if (POSITIVE_NUMBER_TYPING_PATTERN.test(e.target.value)) {
      setNumberText(e.target.value);
    }
  };

  const handleNumberKeyDown = e => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const positiveNumberText = normalizePositiveNumberText(numberText);
      if (positiveNumberText) {
        appendToken({ kind: TOKEN_KIND.number, value: positiveNumberText });
        setNumberText('');
      }
      return;
    }
    if (e.key === 'Backspace' && numberText === '' && tokens.length) {
      removeTokenAt(tokens.length - 1);
    }
  };

  return (
    <div className="mt-2">
      <div
        className="d-flex flex-wrap align-items-center mb-3"
        style={{
          border: `1px solid ${borderColor}`,
          borderRadius: '5px',
          padding: '8px',
          gap: '5px',
          minHeight: '44px'
        }}
      >
        {tokens.map((token, index) => (
          <Badge
            key={index}
            bg={TOKEN_BADGE_BG[token.kind]}
            title="Click to remove"
            style={{ cursor: 'pointer', fontSize: '0.8rem' }}
            onClick={() => removeTokenAt(index)}
          >
            {tokenLabel(token)}
          </Badge>
        ))}
        <input
          type="text"
          inputMode="decimal"
          placeholder="Type a number, press Enter"
          value={numberText}
          onChange={handleNumberTextChange}
          onKeyDown={handleNumberKeyDown}
          style={{
            flex: 1,
            minWidth: '180px',
            border: 'none',
            outline: 'none',
            background: 'transparent',
            color: 'inherit',
            fontSize: '0.85rem'
          }}
        />
        <Button
          variant="danger"
          size="sm"
          onClick={() => applyTokens([])}
          disabled={!tokens.length}
        >
          Clear
        </Button>
      </div>

      <div className="mb-3">
        <div className="fs--1 fw-semi-bold mb-1">Operators</div>
        <div className="d-flex flex-wrap" style={{ gap: '8px' }}>
          {FORMULA_OPERATORS.map(operator => (
            <Button
              key={operator}
              variant="outline-secondary"
              size="sm"
              style={{ minWidth: '36px' }}
              onClick={() =>
                appendToken({ kind: TOKEN_KIND.operator, value: operator })
              }
            >
              {operator}
            </Button>
          ))}
        </div>
      </div>

      <div>
        <div className="fs--1 fw-semi-bold mb-1">Measurables</div>
        <div
          className="d-flex flex-wrap"
          style={{
            border: `1px solid ${borderColor}`,
            borderRadius: '5px',
            padding: '8px',
            gap: '5px'
          }}
        >
          {fields.length ? (
            fields.map(field => (
              <Badge
                key={field.id}
                bg="primary"
                style={{ cursor: 'pointer', fontSize: '0.8rem' }}
                onClick={() =>
                  appendToken({ kind: TOKEN_KIND.field, fieldId: field.id })
                }
              >
                {field.name}
              </Badge>
            ))
          ) : (
            <span className="fs--1 text-muted">
              No other measurables with the same target type.
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

SicFormulaEditor.propTypes = {
  formulaWithIds: PropTypes.string,
  fields: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired
    })
  ).isRequired,
  onChange: PropTypes.func.isRequired
};
